import { useState } from 'react';
import { X, AlertCircle, Package, Percent, AlertTriangle, ArrowRightLeft, Users, Star, Gift, Check, DollarSign, User, Phone, ChefHat, MessageSquare, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

// Weight Input Modal for products sold by weight
export const WeightModal = ({ 
  isOpen, 
  product, 
  weightInput, 
  setWeightInput, 
  onConfirm, 
  onClose 
}) => {
  if (!isOpen || !product) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="weight-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="bg-blue-500 text-white px-4 py-3 flex items-center gap-2">
          <Package className="w-5 h-5" />
          <h3 className="font-bold">Cantidad por Peso</h3>
        </div>
        <div className="p-4">
          <div className="text-center mb-4">
            <p className="font-bold text-lg">{product.name}</p>
            <p className="text-blue-600 font-semibold">${product.price} por {product.weight_unit || 'kg'}</p>
          </div>
          
          <div className="mb-4">
            <Label className="text-sm font-medium mb-2 block">
              Cantidad ({product.weight_unit || 'kg'})
            </Label>
            <div className="flex gap-2">
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={weightInput}
                onChange={(e) => setWeightInput(e.target.value)}
                placeholder="Ej: 0.5, 1, 1.5"
                className="text-lg font-bold text-center"
                autoFocus
                data-testid="weight-input"
                onKeyDown={(e) => e.key === 'Enter' && onConfirm()}
              />
            </div>
            
            <div className="grid grid-cols-4 gap-2 mt-3">
              {['0.25', '0.5', '0.75', '1'].map(w => (
                <Button
                  key={w}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setWeightInput(w)}
                  className="text-xs"
                >
                  {w} {product.weight_unit || 'kg'}
                </Button>
              ))}
            </div>
          </div>
          
          {weightInput && parseFloat(weightInput) > 0 && (
            <div className="bg-green-50 border border-green-200 rounded p-3 mb-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total:</span>
                <span className="text-xl font-bold text-green-600">
                  ${(product.price * parseFloat(weightInput)).toFixed(2)}
                </span>
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} data-testid="weight-modal-close">
              Cancelar
            </Button>
            <Button
              className="bg-blue-500 hover:bg-blue-600 text-white"
              onClick={onConfirm}
              disabled={!weightInput || parseFloat(weightInput) <= 0}
              data-testid="weight-modal-confirm"
            >
              Agregar al Carrito
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Cancel Item Modal (cart items)
export const CancelItemModal = ({ 
  isOpen, 
  cancelReason, 
  setCancelReason, 
  onConfirm, 
  onClose,
  processing = false 
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="cancel-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
        <div className="bg-red-500 text-white px-4 py-3 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <h3 className="font-bold">Cancelar Producto</h3>
        </div>
        <div className="p-4">
          <p className="text-sm text-gray-600 mb-3">
            Por favor, ingresa el motivo de la cancelación:
          </p>
          <textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Motivo de cancelación..."
            className="w-full border rounded p-2 text-sm h-24 focus:ring-2 focus:ring-red-200 focus:border-red-400"
            autoFocus
            data-testid="cancel-reason-input"
          />
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={onClose} disabled={processing} data-testid="cancel-modal-close">
              Cerrar
            </Button>
            <Button
              className="bg-red-500 hover:bg-red-600 text-white"
              onClick={onConfirm}
              disabled={processing || !cancelReason.trim()}
              data-testid="cancel-modal-confirm"
            >
              {processing ? 'Cancelando...' : 'Confirmar Cancelación'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Cancel Existing Order Item Modal
export const CancelExistingItemModal = ({ 
  isOpen, 
  itemName,
  cancelReason, 
  setCancelReason, 
  onConfirm, 
  onClose,
  processing = false 
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="cancel-existing-item-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
        <div className="bg-red-500 text-white px-4 py-3 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <h3 className="font-bold">Cancelar Producto de la Orden</h3>
        </div>
        <div className="p-4">
          <p className="text-sm text-gray-600 mb-1">Estás a punto de cancelar:</p>
          <p className="font-bold text-lg mb-3 text-gray-800">"{itemName}"</p>
          <p className="text-sm text-gray-600 mb-3">Por favor, ingresa el motivo:</p>
          <textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Ej: Cliente cambió de opinión, error en pedido..."
            className="w-full border rounded p-2 text-sm h-24 focus:ring-2 focus:ring-red-200 focus:border-red-400"
            autoFocus
            data-testid="cancel-existing-reason-input"
          />
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={onClose} disabled={processing}>Cerrar</Button>
            <Button
              className="bg-red-500 hover:bg-red-600 text-white"
              onClick={onConfirm}
              disabled={processing || !cancelReason.trim()}
            >
              {processing ? 'Cancelando...' : 'Confirmar Cancelación'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Void Order Modal
export const VoidOrderModal = ({ 
  isOpen, 
  orderNumber,
  voidReason, 
  setVoidReason, 
  onConfirm, 
  onClose,
  processing = false 
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="void-order-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
        <div className="bg-red-600 text-white px-4 py-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          <h3 className="font-bold">Cancelar Orden #{orderNumber}</h3>
        </div>
        <div className="p-4">
          <p className="text-sm text-gray-600 mb-3">
            Esta acción es irreversible. Por favor, ingresa el motivo:
          </p>
          <textarea
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            placeholder="Motivo de cancelación de la orden..."
            className="w-full border rounded p-2 text-sm h-24 focus:ring-2 focus:ring-red-200 focus:border-red-400"
            autoFocus
            data-testid="void-order-reason-input"
          />
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={onClose} disabled={processing}>Cerrar</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={onConfirm}
              disabled={processing || !voidReason.trim()}
            >
              {processing ? 'Procesando...' : 'Cancelar Orden'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Discount Modal
export const DiscountModal = ({ 
  isOpen, 
  subtotal,
  discountAmount,
  setDiscountAmount,
  discountReason,
  setDiscountReason,
  onConfirm, 
  onClose,
  processing = false 
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="discount-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
        <div className="bg-orange-500 text-white px-4 py-3 flex items-center gap-2">
          <Percent className="w-5 h-5" />
          <h3 className="font-bold">Aplicar Descuento</h3>
        </div>
        <div className="p-4">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">Subtotal actual: <strong>${subtotal?.toFixed(2)}</strong></p>
            <Label className="text-sm font-medium mb-1 block">Monto del descuento ($)</Label>
            <Input
              type="number"
              min="0"
              max={subtotal}
              value={discountAmount || ''}
              onChange={(e) => setDiscountAmount(parseFloat(e.target.value) || 0)}
              placeholder="0.00"
              className="text-lg"
              autoFocus
              data-testid="discount-amount-input"
            />
          </div>
          <div className="mb-4">
            <Label className="text-sm font-medium mb-1 block">Motivo del descuento *</Label>
            <textarea
              value={discountReason}
              onChange={(e) => setDiscountReason(e.target.value)}
              placeholder="Ej: Promoción, cliente frecuente, error en producto..."
              className="w-full border rounded p-2 text-sm h-20"
              data-testid="discount-reason-input"
            />
          </div>
          {discountAmount > 0 && (
            <div className="bg-green-50 border border-green-200 rounded p-3 mb-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Nuevo total:</span>
                <span className="text-xl font-bold text-green-600">
                  ${Math.max(0, subtotal - discountAmount).toFixed(2)}
                </span>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={processing}>Cancelar</Button>
            <Button
              className="bg-orange-500 hover:bg-orange-600 text-white"
              onClick={onConfirm}
              disabled={processing || !discountReason.trim() || discountAmount <= 0 || discountAmount > subtotal}
            >
              {processing ? 'Aplicando...' : 'Aplicar Descuento'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Change Table Modal
export const ChangeTableModal = ({ 
  isOpen, 
  currentTableNumber,
  tables,
  getTableStatus,
  selectedNewTableId,
  setSelectedNewTableId,
  onConfirm, 
  onClose,
  processing = false 
}) => {
  if (!isOpen) return null;

  const availableTables = tables.filter(t => getTableStatus(t) === 'available');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="change-table-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
        <div className="bg-blue-500 text-white px-4 py-3 flex items-center gap-2">
          <ArrowRightLeft className="w-5 h-5" />
          <h3 className="font-bold">Cambiar de Mesa</h3>
        </div>
        <div className="p-4">
          <p className="text-sm text-gray-600 mb-4">
            Mesa actual: <strong>Mesa {currentTableNumber}</strong>
          </p>
          <Label className="text-sm font-medium mb-2 block">Selecciona la nueva mesa:</Label>
          {availableTables.length > 0 ? (
            <div className="grid grid-cols-4 gap-2 max-h-48 overflow-auto">
              {availableTables.map(table => (
                <button
                  key={table.table_id}
                  onClick={() => setSelectedNewTableId(table.table_id)}
                  className={`aspect-square rounded-lg flex items-center justify-center font-bold text-lg transition-all ${
                    selectedNewTableId === table.table_id
                      ? 'bg-blue-500 text-white ring-2 ring-blue-300'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  {table.number}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-center py-4 text-gray-500">No hay mesas disponibles</p>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={onClose} disabled={processing}>Cancelar</Button>
            <Button
              className="bg-blue-500 hover:bg-blue-600 text-white"
              onClick={onConfirm}
              disabled={processing || !selectedNewTableId}
            >
              {processing ? 'Cambiando...' : 'Confirmar Cambio'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Edit Customer Modal
export const EditCustomerModal = ({ 
  isOpen, 
  customerName,
  setCustomerName,
  customerPhone,
  setCustomerPhone,
  editReason,
  setEditReason,
  onConfirm, 
  onClose,
  processing = false 
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="edit-customer-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
        <div className="bg-blue-500 text-white px-4 py-3 flex items-center gap-2">
          <User className="w-5 h-5" />
          <h3 className="font-bold">Editar Información del Cliente</h3>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <Label className="text-sm font-medium mb-1 block">Nombre</Label>
            <Input
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Nombre del cliente"
            />
          </div>
          <div>
            <Label className="text-sm font-medium mb-1 block">Teléfono</Label>
            <Input
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              placeholder="Teléfono"
            />
          </div>
          <div>
            <Label className="text-sm font-medium mb-1 block">Motivo de la edición *</Label>
            <textarea
              value={editReason}
              onChange={(e) => setEditReason(e.target.value)}
              placeholder="Ej: Corrección de datos..."
              className="w-full border rounded p-2 text-sm h-20"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={processing}>Cancelar</Button>
            <Button
              className="bg-blue-500 hover:bg-blue-600 text-white"
              onClick={onConfirm}
              disabled={processing || !editReason.trim()}
            >
              {processing ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Duplicate Customer Modal
export const DuplicateCustomerModal = ({ 
  isOpen, 
  existingCustomer,
  onUseExisting, 
  onCreateNew,
  onCancel 
}) => {
  if (!isOpen || !existingCustomer) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="duplicate-customer-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
        <div className="bg-amber-500 text-white px-4 py-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          <h3 className="font-bold">Cliente Existente Detectado</h3>
        </div>
        <div className="p-4">
          <p className="text-sm text-gray-600 mb-4">
            Ya existe un cliente con este número de teléfono:
          </p>
          <div className="bg-gray-50 rounded-lg p-3 mb-4 border">
            <p className="font-bold">{existingCustomer.name}</p>
            <p className="text-sm text-gray-500 flex items-center gap-1">
              <Phone className="w-3 h-3" /> {existingCustomer.phone}
            </p>
            {existingCustomer.total_spent > 0 && (
              <p className="text-xs text-green-600 mt-1">
                Total gastado: ${existingCustomer.total_spent?.toFixed(0)} | {existingCustomer.visit_count || 0} visitas
              </p>
            )}
          </div>
          <p className="text-sm text-gray-600 mb-4">¿Qué deseas hacer?</p>
          <div className="space-y-2">
            <Button className="w-full bg-green-500 hover:bg-green-600 text-white" onClick={onUseExisting}>
              <Check className="w-4 h-4 mr-2" />
              Usar cliente existente
            </Button>
            <Button variant="outline" className="w-full" onClick={onCreateNew}>
              Crear nuevo cliente de todos modos
            </Button>
            <Button variant="ghost" className="w-full text-gray-500" onClick={onCancel}>
              Cancelar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Notes Modal for product notes
export const NotesModal = ({ 
  isOpen, 
  product,
  productNotes,
  setProductNotes,
  selectedQuickNotes,
  toggleQuickNote,
  quickNotes,
  onConfirm, 
  onQuickAdd,
  onClose 
}) => {
  if (!isOpen || !product) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="notes-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
        <div className="bg-orange-500 text-white px-4 py-3">
          <h3 className="font-bold">{product.name}</h3>
          <p className="text-sm opacity-90">${product.price?.toFixed(2)}</p>
        </div>
        <div className="p-4">
          <Label className="text-sm font-medium mb-2 block">Notas rápidas:</Label>
          <div className="flex flex-wrap gap-2 mb-4">
            {quickNotes?.map(note => (
              <button
                key={note.id}
                onClick={() => toggleQuickNote(note.label)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  selectedQuickNotes.includes(note.label)
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                {note.label}
              </button>
            ))}
          </div>
          <Label className="text-sm font-medium mb-1 block">Nota personalizada:</Label>
          <textarea
            value={productNotes}
            onChange={(e) => setProductNotes(e.target.value)}
            placeholder="Ej: Sin cebolla, bien cocido..."
            className="w-full border rounded p-2 text-sm h-20 mb-4"
          />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button variant="outline" onClick={onQuickAdd} className="flex-1">
              Sin notas
            </Button>
            <Button className="flex-1 bg-orange-500 hover:bg-orange-600 text-white" onClick={onConfirm}>
              Agregar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Ingredient Selection Modal
export const IngredientModal = ({ 
  isOpen, 
  product,
  selectedIngredients,
  setSelectedIngredients,
  onConfirm, 
  onClose 
}) => {
  if (!isOpen || !product) return null;

  const toggleIngredient = (ingredientId) => {
    setSelectedIngredients(prev => ({
      ...prev,
      [ingredientId]: !prev[ingredientId]
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="ingredient-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
        <div className="bg-green-500 text-white px-4 py-3">
          <h3 className="font-bold">{product.name}</h3>
          <p className="text-sm opacity-90">Selecciona los ingredientes</p>
        </div>
        <div className="p-4">
          <div className="space-y-2 max-h-64 overflow-auto">
            {product.selectable_ingredients?.map(ing => (
              <label
                key={ing.processed_id}
                className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedIngredients[ing.processed_id]
                    ? 'bg-green-50 border-green-300'
                    : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedIngredients[ing.processed_id] || false}
                  onChange={() => toggleIngredient(ing.processed_id)}
                  className="w-5 h-5 rounded border-gray-300 text-green-500 focus:ring-green-500"
                />
                <span className="ml-3 font-medium">{ing.name}</span>
              </label>
            ))}
          </div>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={onClose} className="flex-1">Cancelar</Button>
            <Button className="flex-1 bg-green-500 hover:bg-green-600 text-white" onClick={onConfirm}>
              Confirmar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Loyalty Redemption Modal
export const RedeemModal = ({ 
  isOpen, 
  redeemCode,
  setRedeemCode,
  redeemPin,
  setRedeemPin,
  pendingRedemptions,
  onVerify, 
  onClose,
  loading = false 
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Gift className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold">Canjear Puntos</h2>
                <p className="text-white/80 text-sm">Verificar canje de cliente</p>
              </div>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {pendingRedemptions?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm font-medium text-amber-800 mb-2">
                🔔 {pendingRedemptions.length} canje(s) pendiente(s):
              </p>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {pendingRedemptions.map(r => (
                  <div 
                    key={r.redemption_id} 
                    className="flex justify-between items-center text-sm bg-white p-2 rounded border cursor-pointer hover:bg-amber-50"
                    onClick={() => setRedeemCode(r.redemption_code)}
                  >
                    <div>
                      <span className="font-medium">{r.customer_name}</span>
                      <span className="text-gray-500 ml-2">→ {r.product_name}</span>
                    </div>
                    <span className="font-mono text-amber-600">{r.redemption_code}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label className="text-gray-700 font-medium">Código de Canje</Label>
            <Input
              type="text"
              placeholder="Ej: ABC123"
              value={redeemCode}
              onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
              className="mt-1 text-center text-xl font-mono tracking-widest uppercase"
              maxLength={10}
            />
          </div>

          <div>
            <Label className="text-gray-700 font-medium">PIN del Cliente</Label>
            <Input
              type="text"
              placeholder="****"
              value={redeemPin}
              onChange={(e) => setRedeemPin(e.target.value)}
              className="mt-1 text-center text-xl font-mono tracking-widest"
              maxLength={4}
            />
            <p className="text-xs text-gray-500 mt-1">El cliente recibió este PIN en su app</p>
          </div>
        </div>

        <div className="p-4 bg-gray-50 border-t flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancelar</Button>
          <Button
            onClick={onVerify}
            disabled={loading || !redeemCode.trim() || !redeemPin.trim()}
            className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
          >
            {loading ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Verificando...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Confirmar Canje
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default {
  WeightModal,
  CancelItemModal,
  CancelExistingItemModal,
  VoidOrderModal,
  DiscountModal,
  ChangeTableModal,
  EditCustomerModal,
  DuplicateCustomerModal,
  NotesModal,
  IngredientModal,
  RedeemModal
};
